(() => {
  const existing = window.HumanPrimePremiumSearch;
  if (existing) {
    existing.scan(document);
    return;
  }

  const instances = new WeakMap();

  const initialize = (root) => {
    if (!root || instances.has(root)) return;

    const owner = root.closest('.shopify-section') || root.parentElement;
    const header = owner?.querySelector('[data-smart-header]');
    const openers = Array.from(owner?.querySelectorAll('[data-premium-search-open]') || []);
    const opener = openers[0];
    const dialog = root.querySelector('[role="dialog"]');
    const form = root.querySelector('[data-premium-search-form]');
    const input = root.querySelector('[data-premium-search-input]');
    const results = root.querySelector('[data-premium-search-results]');
    const live = root.querySelector('[data-premium-search-live]');
    const allResultsLink = root.querySelector('[data-search-all-link]');
    const errorLink = root.querySelector('[data-search-error-link]');
    if (!opener || !dialog || !form || !input || !results) return;

    const abortEvents = new AbortController();
    const signal = abortEvents.signal;
    const stateNodes = Array.from(root.querySelectorAll('[data-search-state]'));
    let requestController = null;
    let debounceTimer = 0;
    let closeTimer = 0;
    let activeIndex = -1;
    let lastOpener = opener;
    let backgroundState = [];

    const setState = (name) => {
      stateNodes.forEach((node) => { node.hidden = node.dataset.searchState !== name; });
      allResultsLink.hidden = name !== 'results';
    };

    const announce = (message) => {
      if (live) live.textContent = message;
    };

    const updateSearchLinks = (query) => {
      const url = new URL(root.dataset.searchUrl, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('type', 'product');
      allResultsLink.href = url.toString();
      if (errorLink) errorLink.href = url.toString();
    };

    const formatMoney = (value) => {
      const numeric = Number.parseFloat(value);
      if (!Number.isFinite(numeric)) return '';
      try {
        return new Intl.NumberFormat('es-PE', { style: 'currency', currency: root.dataset.currency || 'PEN' }).format(numeric);
      } catch (_error) {
        return numeric.toFixed(2);
      }
    };

    const cdnImage = (source, width) => {
      if (!source) return '';
      const url = new URL(source, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    };

    const activateResult = (index) => {
      const links = Array.from(results.querySelectorAll('[role="option"]'));
      if (!links.length) {
        activeIndex = -1;
        return;
      }
      activeIndex = (index + links.length) % links.length;
      links.forEach((link, linkIndex) => {
        const active = linkIndex === activeIndex;
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-selected', String(active));
      });
      links[activeIndex].scrollIntoView({ block: 'nearest' });
      announce(`${links[activeIndex].dataset.productTitle}, resultado ${activeIndex + 1} de ${links.length}`);
    };

    const renderProduct = (product, index) => {
      const link = document.createElement('a');
      link.className = 'hp-search-result';
      link.href = product.url;
      link.setAttribute('role', 'option');
      link.setAttribute('aria-selected', 'false');
      link.dataset.productTitle = product.title || '';
      link.dataset.resultIndex = String(index);

      const media = document.createElement('span');
      media.className = 'hp-search-result__media';
      if (product.featured_image?.url || product.image) {
        const source = product.featured_image?.url || product.image;
        const image = document.createElement('img');
        image.src = cdnImage(source, 160);
        image.srcset = `${cdnImage(source, 96)} 96w, ${cdnImage(source, 160)} 160w, ${cdnImage(source, 240)} 240w`;
        image.sizes = '(max-width: 767px) 68px, 80px';
        image.alt = product.featured_image?.alt || product.title || '';
        image.loading = 'lazy';
        image.width = 160;
        image.height = Math.max(1, Math.round(160 / (product.featured_image?.aspect_ratio || 1)));
        media.append(image);
      }

      const content = document.createElement('span');
      if (product.type) {
        const type = document.createElement('span');
        type.className = 'hp-search-result__type';
        type.textContent = product.type;
        content.append(type);
      }
      const title = document.createElement('h3');
      title.textContent = product.title || '';
      content.append(title);

      const pricing = document.createElement('span');
      pricing.className = 'hp-search-result__pricing';
      const price = document.createElement('span');
      price.textContent = formatMoney(product.price_min || product.price);
      pricing.append(price);
      const compareValue = Number.parseFloat(product.compare_at_price_min);
      const priceValue = Number.parseFloat(product.price_min || product.price);
      if (Number.isFinite(compareValue) && compareValue > priceValue) {
        const compare = document.createElement('s');
        compare.textContent = formatMoney(compareValue);
        pricing.append(compare);
      }
      content.append(pricing);

      const availability = document.createElement('span');
      availability.className = `hp-search-result__availability${product.available ? '' : ' is-sold-out'}`;
      availability.textContent = product.available ? 'Disponible' : 'Agotado';
      content.append(availability);

      const arrow = document.createElement('span');
      arrow.className = 'hp-search-result__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.textContent = '→';

      link.append(media, content, arrow);
      link.addEventListener('pointerenter', () => activateResult(index), { signal });
      link.addEventListener('focus', () => activateResult(index), { signal });
      return link;
    };

    const fetchProducts = async (query) => {
      requestController?.abort();
      requestController = new AbortController();
      setState('loading');
      announce('Buscando productos');
      updateSearchLinks(query);

      try {
        const url = new URL(root.dataset.predictiveUrl, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '6');
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: requestController.signal });
        if (!response.ok) throw new Error('Predictive search request failed');
        const payload = await response.json();
        if (root.hidden || input.value.trim() !== query) return;
        const products = payload?.resources?.results?.products || [];
        results.replaceChildren(...products.map(renderProduct));
        activeIndex = -1;
        if (products.length) {
          setState('results');
          announce(`${products.length} ${products.length === 1 ? 'producto encontrado' : 'productos encontrados'}`);
        } else {
          setState('empty');
          announce('No encontramos productos para esta búsqueda');
        }
      } catch (error) {
        if (error.name === 'AbortError' || root.hidden) return;
        results.replaceChildren();
        setState('error');
        announce('No pudimos cargar los resultados');
      }
    };

    const scheduleSearch = () => {
      window.clearTimeout(debounceTimer);
      requestController?.abort();
      const query = input.value.trim();
      results.replaceChildren();
      activeIndex = -1;
      if (query.length < 2) {
        setState('initial');
        announce(query.length ? 'Escribe al menos dos caracteres' : '');
        return;
      }
      setState('loading');
      debounceTimer = window.setTimeout(() => {
        if (!root.hidden) fetchProducts(query);
      }, 220);
    };

    const makeBackgroundInert = () => {
      const bodyChildren = Array.from(document.body.children).filter((element) => element !== owner);
      const targets = header ? [...bodyChildren, header] : bodyChildren;
      backgroundState = targets.map((element) => ({ element, inert: element.inert, ariaHidden: element.getAttribute('aria-hidden') }));
      targets.forEach((element) => {
        element.inert = true;
        element.setAttribute('aria-hidden', 'true');
      });
    };

    const restoreBackground = () => {
      backgroundState.forEach(({ element, inert, ariaHidden }) => {
        element.inert = inert;
        if (ariaHidden === null) element.removeAttribute('aria-hidden');
        else element.setAttribute('aria-hidden', ariaHidden);
      });
      backgroundState = [];
    };

    const open = (trigger = opener) => {
      window.clearTimeout(closeTimer);
      lastOpener = trigger;
      root.hidden = false;
      openers.forEach((item) => item.setAttribute('aria-expanded', String(item === trigger)));
      makeBackgroundInert();
      document.body.classList.add('hp-search-open');
      requestAnimationFrame(() => {
        root.classList.add('is-open');
        input.focus({ preventScroll: true });
      });
    };

    const close = () => {
      window.clearTimeout(debounceTimer);
      requestController?.abort();
      root.classList.remove('is-open');
      openers.forEach((item) => item.setAttribute('aria-expanded', 'false'));
      restoreBackground();
      document.body.classList.remove('hp-search-open');
      closeTimer = window.setTimeout(() => {
        root.hidden = true;
        lastOpener?.focus({ preventScroll: true });
      }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 220);
    };

    const focusableElements = () => Array.from(dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden && element.getClientRects().length > 0);

    openers.forEach((item) => item.addEventListener('click', (event) => {
      event.preventDefault();
      open(event.currentTarget);
    }, { signal }));
    root.querySelectorAll('[data-premium-search-close]').forEach((button) => button.addEventListener('click', close, { signal }));
    input.addEventListener('input', scheduleSearch, { signal });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const links = results.querySelectorAll('[role="option"]');
        if (!links.length) return;
        event.preventDefault();
        activateResult(activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
        return;
      }
      if (event.key === 'Enter' && activeIndex >= 0) {
        const active = results.querySelectorAll('[role="option"]')[activeIndex];
        if (active) {
          event.preventDefault();
          active.click();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    instances.set(root, {
      destroy() {
        window.clearTimeout(debounceTimer);
        window.clearTimeout(closeTimer);
        requestController?.abort();
        if (!root.hidden) restoreBackground();
        document.body.classList.remove('hp-search-open');
        abortEvents.abort();
      }
    });
  };

  const destroy = (root) => {
    const instance = instances.get(root);
    if (!instance) return;
    instance.destroy();
    instances.delete(root);
  };

  const scan = (scope) => {
    if (scope.matches?.('[data-premium-search]')) initialize(scope);
    scope.querySelectorAll?.('[data-premium-search]').forEach(initialize);
  };

  window.HumanPrimePremiumSearch = { scan, destroy };
  scan(document);
  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.matches?.('[data-premium-search]') ? event.target : event.target.querySelector?.('[data-premium-search]');
    destroy(root);
  });
})();
